import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';

interface KillConfirmationDialogProps {
  open: boolean;
  orgName: string;
  orgId: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const KillConfirmationDialog: React.FC<KillConfirmationDialogProps> = ({
  open,
  orgName,
  orgId,
  isPending,
  onConfirm,
  onCancel,
}) => {
  const [confirmText, setConfirmText] = useState('');

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setConfirmText('');
      onCancel();
    }
  };

  const handleConfirm = () => {
    if (confirmText === 'KILL') {
      onConfirm();
      setConfirmText('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Kill All Active Invocations</DialogTitle>
          <DialogDescription>
            This will kill all active invocations for{' '}
            <span className="font-semibold text-foreground">{orgName}</span>.
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-mono">{orgId}</p>
          <Label htmlFor="kill-confirm-input" className="text-sm">
            Type <span className="font-mono font-semibold">KILL</span> to
            confirm
          </Label>
          <Input
            id="kill-confirm-input"
            autoFocus
            placeholder="KILL"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && confirmText === 'KILL') {
                handleConfirm();
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={confirmText !== 'KILL' || isPending}
            onClick={handleConfirm}
          >
            {isPending ? 'Killing...' : 'Kill Invocations'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
